'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, useReducedMotion } from 'motion/react';
import type { CSSProperties, KeyboardEvent, PointerEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Container } from '@/app/components/layouts/container';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { SpartanProductExteriorThreesixtyCanvas } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/canvas';
import type { SpartanProductExteriorThreesixtyCanvasHandle } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/canvas/types';
import { SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_LABELS, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_REFERENCE_FRAME_COUNT, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_SHARP_FRAME_DEBOUNCE_MS } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/constants';
import type { SpartanProductExteriorThreesixtyBackground, SpartanProductExteriorThreesixtyDragCursorTone, SpartanProductExteriorThreesixtyLoader, SpartanProductExteriorThreesixtyRotationDirection } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/types';
import { getShortestThreesixtyFrameDelta, getThreesixtyStageBackground, wrapThreesixtyIndex } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/utils';
import { SpartanProductExteriorThreesixtyDragCursor } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/viewer/drag-cursor';
import { SpartanProductExteriorThreesixtyDragGuide } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/viewer/drag-guide';
import type { SpartanProductExteriorThreesixtyDragGuideTone } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/viewer/drag-guide/types';
import { SpartanProductExteriorThreesixtyPresetThumbnails } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/viewer/preset-thumbnails';
import { SpartanProductExteriorThreesixtyPresetThumbnailPreloads } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/viewer/preset-thumbnails/preloads';
import { useThreesixtyLoadingPhase } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/viewer/useThreesixtyLoadingPhase';
import { SPARTAN_PRODUCT_EXTERIOR_DATA_GA, SPARTAN_PRODUCT_EXTERIOR_TRACKING } from '@/app/lcv/components/sections/spartan/products/exterior/tracking';
import { SpartanProductExteriorInlineLoadingStatus } from '@/app/lcv/components/sections/spartan/products/exterior-inline/threesixty/viewer/loading-status';
import { useInlineThreesixtyRotation } from '@/app/lcv/components/sections/spartan/products/exterior-inline/threesixty/viewer/useInlineThreesixtyRotation';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { isDataLayerAvailable, trackDynamicEvents } from '@/tag-manager';
const EMPTY_PRESET_INDEXES: number[] = [];
const PRESET_CRUISE_FRAMES_PER_SECOND = 68;
const PRESET_MIN_DURATION_MS = 32;
const PRESET_SNAP_MAX_REFERENCE_FRAMES = 2;
// The inline viewer starts preset turns only after the complete sequence loads.
const PRESET_CANVAS_DRAW_OPTIONS = {
  notifyLoader: false
} as const;
interface InlineControlsStyle extends CSSProperties {
  '--inline-threesixty-controls-background'?: string;
}
interface SpartanProductExteriorInlineViewerProps {
  frames: string[];
  presetIndexes?: readonly number[];
  alt: string;
  background?: SpartanProductExteriorThreesixtyBackground;
  loader: SpartanProductExteriorThreesixtyLoader;
  isPreloadEnabled?: boolean;
  isInteractionEnabled: boolean;
  dragCursorTone?: SpartanProductExteriorThreesixtyDragCursorTone;
  dragGuideTone?: SpartanProductExteriorThreesixtyDragGuideTone;
  onCanvasReady?: () => void;
}
const clampDragCursorCoordinate = (coordinate: number, size: number) => {
  const radius = window.innerWidth < 768 ? 40 : 46;
  if (!Number.isFinite(size) || size <= 0) return coordinate;
  if (size <= radius * 2) return size / 2;
  return Math.min(Math.max(coordinate, radius), size - radius);
};
export const SpartanProductExteriorInlineViewer = t0 => {
  const $ = _c(129);
  const {
    frames,
    presetIndexes: t1,
    alt,
    background,
    loader,
    isPreloadEnabled: t2,
    isInteractionEnabled,
    dragCursorTone: t3,
    dragGuideTone,
    onCanvasReady
  } = t0;
  const presetIndexes = t1 === undefined ? EMPTY_PRESET_INDEXES : t1;
  const isPreloadEnabled = t2 === undefined ? true : t2;
  const dragCursorTone = t3 === undefined ? "default" : t3;
  const {
    isInteractionReady,
    loadState,
    requestSharpFrame,
    cancelSharpFrameRequest,
    setCurrentIndex
  } = loader;
  let t4;
  if ($[0] !== frames) {
    t4 = frames.join("\n");
    $[0] = frames;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const framesKey = t4;
  const mediaElementRef = useRef(null);
  const viewerElementRef = useRef(null);
  const dragCursorRef = useRef(null);
  const canvasRef = useRef(null);
  const currentIndexRef = useRef(0);
  const lastFinePointerPositionRef = useRef(null);
  const sharpFrameTimeoutRef = useRef(null);
  const presetAnimationFrameRef = useRef(null);
  const presetTargetIndexRef = useRef(null);
  const hasVisibleStatusRef = useRef(false);
  const hasTrackedDragRef = useRef(false);
  const [activePresetIndex, setActivePresetIndex] = useState(presetIndexes[0] ?? null);
  const [isPresetRotating, setIsPresetRotating] = useState(false);
  const [isPointerInside, setIsPointerInside] = useState(false);
  const [stagePortalTarget, setStagePortalTarget] = useState(null);
  const [interactionPortalTarget, setInteractionPortalTarget] = useState(null);
  const [controlsPortalTarget, setControlsPortalTarget] = useState(null);
  const [isStatusExitComplete, setIsStatusExitComplete] = useState(true);
  const shouldReduceMotion = useReducedMotion() ?? false;
  const hasFinePointer = useMediaQuery("(hover: hover) and (pointer: fine)");
  const t5 = isPreloadEnabled ? loadState : "ready";
  let t6;
  if ($[2] !== t5) {
    t6 = {
      loadState: t5
    };
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const statusPhase = useThreesixtyLoadingPhase(t6);
  let t7;
  if ($[4] !== background) {
    t7 = getThreesixtyStageBackground(background);
    $[4] = background;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const stageBackground = t7;
  const controlsBackground = typeof background === "string" ? background : background?.bottom;
  let t8;
  if ($[6] !== controlsBackground) {
    t8 = controlsBackground ? {
      "--inline-threesixty-controls-background": controlsBackground
    } : undefined;
    $[6] = controlsBackground;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const controlsStyle = t8;
  let t10;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const mediaElement = mediaElementRef.current;
      const stageElement = mediaElement?.closest(`[data-test="${SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyStage}"]`) ?? mediaElement;
      setStagePortalTarget(stageElement);
      setInteractionPortalTarget(stageElement?.parentElement ?? stageElement);
      const trackElement = mediaElement?.closest(`[data-test="${SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyTrack}"]`);
      setControlsPortalTarget(trackElement?.querySelector(`[data-test="${SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyControlsHost}"]`) ?? mediaElement);
    };
    t10 = [];
    $[8] = t10;
    $[9] = t9;
  } else {
    t10 = $[8];
    t9 = $[9];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[10] !== statusPhase) {
    t11 = () => {
      if (statusPhase) {
        hasVisibleStatusRef.current = true;
        setIsStatusExitComplete(false);
        return;
      }
      if (!hasVisibleStatusRef.current) {
        setIsStatusExitComplete(true);
      }
    };
    t12 = [statusPhase];
    $[10] = statusPhase;
    $[11] = t11;
    $[12] = t12;
  } else {
    t11 = $[11];
    t12 = $[12];
  }
  useEffect(t11, t12);
  let t13;
  if ($[13] !== statusPhase) {
    t13 = () => {
      if (statusPhase !== null) {
        return;
      }
      hasVisibleStatusRef.current = false;
      setIsStatusExitComplete(true);
    };
    $[13] = statusPhase;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const handleLoadingStatusExitComplete = t13;
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      if (sharpFrameTimeoutRef.current === null) {
        return;
      }
      window.clearTimeout(sharpFrameTimeoutRef.current);
      sharpFrameTimeoutRef.current = null;
    };
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  const cancelPendingSharpFrame = t14;
  let t15;
  if ($[16] !== cancelSharpFrameRequest) {
    t15 = () => {
      cancelPendingSharpFrame();
      cancelSharpFrameRequest?.();
    };
    $[16] = cancelSharpFrameRequest;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  const cancelSharpFrameUpgrade = t15;
  let t16;
  if ($[18] !== requestSharpFrame) {
    t16 = index => {
      cancelPendingSharpFrame();
      sharpFrameTimeoutRef.current = window.setTimeout(() => {
        sharpFrameTimeoutRef.current = null;
        requestSharpFrame?.(index);
      }, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_SHARP_FRAME_DEBOUNCE_MS);
    };
    $[18] = requestSharpFrame;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const scheduleSharpFrame = t16;
  let t17;
  if ($[20] !== setCurrentIndex) {
    t17 = (nextIndex, direction) => {
      currentIndexRef.current = nextIndex;
      setCurrentIndex(nextIndex, direction);
      canvasRef.current?.drawIndex(nextIndex);
    };
    $[20] = setCurrentIndex;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  const handleRotationIndexChange = t17;
  let t18;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => canvasRef.current?.getDisplayedIndex() ?? currentIndexRef.current;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  const getDisplayedIndex = t18;
  let t19;
  let t20;
  if ($[23] !== isInteractionEnabled || $[24] !== isInteractionReady || $[25] !== requestSharpFrame) {
    t19 = () => {
      let resizeAnimationFrameId = null;
      let sharpFrameTimeoutId = null;
      const handleResize = () => {
        if (resizeAnimationFrameId !== null) {
          window.cancelAnimationFrame(resizeAnimationFrameId);
        }
        if (sharpFrameTimeoutId !== null) {
          window.clearTimeout(sharpFrameTimeoutId);
        }
        resizeAnimationFrameId = window.requestAnimationFrame(() => {
          resizeAnimationFrameId = null;
          canvasRef.current?.resize();
        });
        if (!isInteractionReady || !isInteractionEnabled) {
          return;
        }
        sharpFrameTimeoutId = window.setTimeout(() => {
          sharpFrameTimeoutId = null;
          canvasRef.current?.resize();
          requestSharpFrame?.(getDisplayedIndex());
        }, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_SHARP_FRAME_DEBOUNCE_MS);
      };
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        if (resizeAnimationFrameId !== null) {
          window.cancelAnimationFrame(resizeAnimationFrameId);
        }
        if (sharpFrameTimeoutId !== null) {
          window.clearTimeout(sharpFrameTimeoutId);
        }
      };
    };
    t20 = [getDisplayedIndex, isInteractionEnabled, isInteractionReady, requestSharpFrame];
    $[23] = isInteractionEnabled;
    $[24] = isInteractionReady;
    $[25] = requestSharpFrame;
    $[26] = t19;
    $[27] = t20;
  } else {
    t19 = $[26];
    t20 = $[27];
  }
  useEffect(t19, t20);
  let t21;
  if ($[28] !== setCurrentIndex) {
    t21 = direction_0 => {
      setCurrentIndex(getDisplayedIndex(), direction_0);
    };
    $[28] = setCurrentIndex;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  const handleRotationDirectionChange = t21;
  let t22;
  if ($[30] !== presetIndexes || $[31] !== scheduleSharpFrame) {
    t22 = index_0 => {
      scheduleSharpFrame(index_0);
      setActivePresetIndex(presetIndexes.includes(index_0) ? index_0 : null);
    };
    $[30] = presetIndexes;
    $[31] = scheduleSharpFrame;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  const handleInteractionEnd = t22;
  let t23;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = (distanceInFrames, pointerType) => {
      if (hasTrackedDragRef.current || distanceInFrames < 3 || !isDataLayerAvailable()) {
        return;
      }
      hasTrackedDragRef.current = true;
      trackDynamicEvents({
        event_name: SPARTAN_PRODUCT_EXTERIOR_DATA_GA.drag,
        group_name: SPARTAN_PRODUCT_EXTERIOR_DATA_GA.section,
        step_name: "threesixty",
        value: pointerType
      });
    };
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  const handleDragEnd = t23;
  let t24;
  if ($[34] !== cancelSharpFrameUpgrade) {
    t24 = () => {
      cancelSharpFrameUpgrade();
      setActivePresetIndex(null);
    };
    $[34] = cancelSharpFrameUpgrade;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] !== handleInteractionEnd || $[37] !== handleRotationDirectionChange || $[38] !== handleRotationIndexChange || $[39] !== shouldReduceMotion || $[40] !== t24) {
    t25 = {
      reducedMotion: shouldReduceMotion,
      getDisplayedIndex,
      onDirectionChange: handleRotationDirectionChange,
      onIndexChange: handleRotationIndexChange,
      onInteractionStart: t24,
      onInteractionEnd: handleInteractionEnd,
      onDragEnd: handleDragEnd
    };
    $[36] = handleInteractionEnd;
    $[37] = handleRotationDirectionChange;
    $[38] = handleRotationIndexChange;
    $[39] = shouldReduceMotion;
    $[40] = t24;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  const rotation = useInlineThreesixtyRotation(frames.length, isInteractionReady && isInteractionEnabled, t25);
  let t26;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => {
      if (presetAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(presetAnimationFrameRef.current);
        presetAnimationFrameRef.current = null;
      }
      presetTargetIndexRef.current = null;
      setIsPresetRotating(false);
    };
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  const cancelPresetRotation = t26;
  let t27;
  if ($[43] !== requestSharpFrame) {
    t27 = () => {
      if (presetAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(presetAnimationFrameRef.current);
        presetAnimationFrameRef.current = null;
      }
      const targetIndex = presetTargetIndexRef.current;
      presetTargetIndexRef.current = null;
      setIsPresetRotating(false);
      if (targetIndex !== null) {
        setActivePresetIndex(targetIndex);
        requestSharpFrame?.(targetIndex);
      }
    };
    $[43] = requestSharpFrame;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  const finishPresetRotation = t27;
  let t28;
  if ($[45] !== cancelSharpFrameUpgrade || $[46] !== finishPresetRotation || $[47] !== frames.length || $[48] !== isInteractionEnabled || $[49] !== isPresetRotating || $[50] !== requestSharpFrame || $[51] !== rotation || $[52] !== shouldReduceMotion) {
    t28 = targetIndex_0 => {
      if (!isInteractionEnabled || isPresetRotating) {
        return;
      }
      cancelSharpFrameUpgrade();
      rotation.stop();
      const startIndex = getDisplayedIndex();
      const delta = getShortestThreesixtyFrameDelta(startIndex, targetIndex_0, frames.length);
      const direction_1 = delta === 0 ? 0 : delta > 0 ? 1 : -1;
      const distance = Math.abs(delta);
      setActivePresetIndex(null);
      if (shouldReduceMotion || distance * SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_REFERENCE_FRAME_COUNT <= PRESET_SNAP_MAX_REFERENCE_FRAMES * frames.length) {
        rotation.setIndex(targetIndex_0, direction_1);
        setActivePresetIndex(targetIndex_0);
        requestSharpFrame?.(targetIndex_0);
        return;
      }
      const duration = Math.max(PRESET_MIN_DURATION_MS, distance * SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_REFERENCE_FRAME_COUNT * 1000 / (PRESET_CRUISE_FRAMES_PER_SECOND * frames.length));
      const startTime = performance.now();
      let lastIndex = startIndex;
      presetTargetIndexRef.current = targetIndex_0;
      setIsPresetRotating(true);
      const drawNextFrame = timestamp => {
        presetAnimationFrameRef.current = null;
        const progress = Math.min(1, Math.max(0, (timestamp - startTime) / duration));
        const traveledFrames = Math.round(distance * progress);
        const nextIndex_0 = wrapThreesixtyIndex(startIndex + direction_1 * traveledFrames, frames.length);
        if (progress >= 1 || nextIndex_0 === targetIndex_0) {
          rotation.setIndex(targetIndex_0, direction_1);
          finishPresetRotation();
          return;
        }
        if (nextIndex_0 !== lastIndex) {
          lastIndex = nextIndex_0;
          canvasRef.current?.drawIndex(nextIndex_0, PRESET_CANVAS_DRAW_OPTIONS);
        }
        presetAnimationFrameRef.current = window.requestAnimationFrame(drawNextFrame);
      };
      presetAnimationFrameRef.current = window.requestAnimationFrame(drawNextFrame);
    };
    $[45] = cancelSharpFrameUpgrade;
    $[46] = finishPresetRotation;
    $[47] = frames.length;
    $[48] = isInteractionEnabled;
    $[49] = isPresetRotating;
    $[50] = requestSharpFrame;
    $[51] = rotation;
    $[52] = shouldReduceMotion;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  const rotateToPreset = t28;
  let t29;
  if ($[54] !== presetIndexes[0]) {
    t29 = () => {
      setActivePresetIndex(presetIndexes[0] ?? null);
    };
    $[54] = presetIndexes[0];
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== framesKey || $[57] !== presetIndexes) {
    t30 = [framesKey, presetIndexes];
    $[56] = framesKey;
    $[57] = presetIndexes;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  useEffect(t29, t30);
  let t31;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => {
      hasTrackedDragRef.current = false;
    };
    $[59] = t31;
  } else {
    t31 = $[59];
  }
  let t32;
  if ($[60] !== framesKey) {
    t32 = [framesKey];
    $[60] = framesKey;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  useEffect(t31, t32);
  let t33;
  let t34;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => () => {
      cancelPresetRotation();
      cancelPendingSharpFrame();
    };
    t34 = [cancelPendingSharpFrame, cancelPresetRotation];
    $[62] = t33;
    $[63] = t34;
  } else {
    t33 = $[62];
    t34 = $[63];
  }
  useEffect(t33, t34);
  let t35;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = (clientX, clientY) => {
      const viewerElement = viewerElementRef.current;
      const dragCursor = dragCursorRef.current;
      if (!viewerElement || !dragCursor) {
        return;
      }
      const bounds = viewerElement.getBoundingClientRect();
      const x = clampDragCursorCoordinate(clientX - bounds.left, bounds.width);
      const y = clampDragCursorCoordinate(clientY - bounds.top, bounds.height);
      dragCursor.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    };
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  const positionDragCursor = t35;
  let t36;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = event => {
      if (event.pointerType === "touch") {
        return;
      }
      lastFinePointerPositionRef.current = {
        clientX: event.clientX,
        clientY: event.clientY
      };
      positionDragCursor(event.clientX, event.clientY);
    };
    $[65] = t36;
  } else {
    t36 = $[65];
  }
  const updateDragCursorPosition = t36;
  let t37;
  let t38;
  if ($[66] !== hasFinePointer || $[67] !== isInteractionEnabled) {
    t37 = () => {
      if (!isInteractionEnabled || !hasFinePointer) {
        return;
      }
      const pointerPosition = lastFinePointerPositionRef.current;
      if (!pointerPosition) {
        return;
      }
      positionDragCursor(pointerPosition.clientX, pointerPosition.clientY);
    };
    t38 = [hasFinePointer, isInteractionEnabled, positionDragCursor];
    $[66] = hasFinePointer;
    $[67] = isInteractionEnabled;
    $[68] = t37;
    $[69] = t38;
  } else {
    t37 = $[68];
    t38 = $[69];
  }
  useLayoutEffect(t37, t38);
  const isInteractive = Boolean(isInteractionReady && isInteractionEnabled && !isPresetRotating && statusPhase === null && isStatusExitComplete);
  const shouldRevealPresets = Boolean(isInteractionReady && isInteractionEnabled && statusPhase === null && isStatusExitComplete);
  const useCustomDragCursor = hasFinePointer === true;
  const isDragGuideVisible = Boolean(isInteractive && !rotation.isDragging && !useCustomDragCursor);
  let t39;
  if ($[70] !== isInteractive || $[71] !== rotation) {
    t39 = event_0 => {
      if (!isInteractive) {
        return;
      }
      if (event_0.pointerType !== "touch") {
        updateDragCursorPosition(event_0);
      }
      rotation.handlePointerDown(event_0);
    };
    $[70] = isInteractive;
    $[71] = rotation;
    $[72] = t39;
  } else {
    t39 = $[72];
  }
  const handleViewerPointerDown = t39;
  let t40;
  if ($[73] !== rotation) {
    t40 = event_1 => {
      updateDragCursorPosition(event_1);
      rotation.handlePointerMove(event_1);
    };
    $[73] = rotation;
    $[74] = t40;
  } else {
    t40 = $[74];
  }
  const handleViewerPointerMove = t40;
  let t41;
  if ($[75] !== isInteractive || $[76] !== rotation) {
    t41 = event_2 => {
      if (!isInteractive) {
        return;
      }
      rotation.handleKeyDown(event_2);
    };
    $[75] = isInteractive;
    $[76] = rotation;
    $[77] = t41;
  } else {
    t41 = $[77];
  }
  const handleViewerKeyDown = t41;
  const handlePresetInteractionStart = rotation.stop;
  let t42;
  if ($[78] !== stageBackground) {
    t42 = stageBackground ? {
      background: stageBackground
    } : undefined;
    $[78] = stageBackground;
    $[79] = t42;
  } else {
    t42 = $[79];
  }
  const t43 = !isInteractionReady;
  let t44;
  if ($[80] !== frames || $[81] !== isPreloadEnabled || $[82] !== presetIndexes) {
    t44 = <SpartanProductExteriorThreesixtyPresetThumbnailPreloads frames={frames} indexes={presetIndexes} enabled={isPreloadEnabled} />;
    $[80] = frames;
    $[81] = isPreloadEnabled;
    $[82] = presetIndexes;
    $[83] = t44;
  } else {
    t44 = $[83];
  }
  let t45;
  if ($[84] !== alt || $[85] !== framesKey || $[86] !== loader || $[87] !== onCanvasReady) {
    t45 = <SpartanProductExteriorThreesixtyCanvas ref={canvasRef} loader={loader} alt={alt} sourceKey={framesKey} testId={SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyViewerCanvas} onReady={onCanvasReady} className="pointer-events-none shrink-0" />;
    $[84] = alt;
    $[85] = framesKey;
    $[86] = loader;
    $[87] = onCanvasReady;
    $[88] = t45;
  } else {
    t45 = $[88];
  }
  let t46;
  if ($[89] !== dragCursorTone || $[90] !== handleViewerKeyDown || $[91] !== handleViewerPointerDown || $[92] !== handleViewerPointerMove || $[93] !== interactionPortalTarget || $[94] !== isInteractive || $[95] !== isPointerInside || $[96] !== rotation.handleLostPointerCapture || $[97] !== rotation.handlePointerCancel || $[98] !== rotation.handlePointerUp || $[99] !== rotation.isDragging || $[100] !== useCustomDragCursor) {
    t46 = interactionPortalTarget ? createPortal(<div ref={viewerElementRef} role="group" tabIndex={isInteractive ? 0 : -1} aria-label={SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_LABELS.viewer} aria-busy={!isInteractive} aria-disabled={!isInteractive} onPointerEnter={event_3 => {
      if (event_3.pointerType === "touch") {
        return;
      }
      updateDragCursorPosition(event_3);
      setIsPointerInside(true);
    }} onPointerLeave={event_4 => {
      if (event_4.pointerType !== "touch") {
        setIsPointerInside(false);
      }
    }} onPointerDown={handleViewerPointerDown} onPointerMove={handleViewerPointerMove} onPointerUp={rotation.handlePointerUp} onPointerCancel={rotation.handlePointerCancel} onLostPointerCapture={rotation.handleLostPointerCapture} onKeyDown={handleViewerKeyDown} className={cn("absolute inset-0 z-30 touch-pan-y select-none", {
      "pointer-events-auto": isInteractive,
      "pointer-events-none": !isInteractive,
      "cursor-none": isInteractive && useCustomDragCursor && !rotation.isDragging,
      "cursor-grabbing": isInteractive && useCustomDragCursor && rotation.isDragging,
      "cursor-default": !isInteractive || !useCustomDragCursor
    })} {...createDataAttributes({
      test: SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyViewer
    })}><SpartanProductExteriorThreesixtyDragCursor ref={dragCursorRef} isEnabled={useCustomDragCursor} isVisible={Boolean(isInteractive && useCustomDragCursor && isPointerInside && !rotation.isDragging)} tone={dragCursorTone} /></div>, interactionPortalTarget) : null;
    $[89] = dragCursorTone;
    $[90] = handleViewerKeyDown;
    $[91] = handleViewerPointerDown;
    $[92] = handleViewerPointerMove;
    $[93] = interactionPortalTarget;
    $[94] = isInteractive;
    $[95] = isPointerInside;
    $[96] = rotation.handleLostPointerCapture;
    $[97] = rotation.handlePointerCancel;
    $[98] = rotation.handlePointerUp;
    $[99] = rotation.isDragging;
    $[100] = useCustomDragCursor;
    $[101] = t46;
  } else {
    t46 = $[101];
  }
  let t47;
  if ($[102] !== handleLoadingStatusExitComplete || $[103] !== stagePortalTarget || $[104] !== statusPhase) {
    t47 = stagePortalTarget ? createPortal(<SpartanProductExteriorInlineLoadingStatus phase={statusPhase} onExitComplete={handleLoadingStatusExitComplete} />, stagePortalTarget) : null;
    $[102] = handleLoadingStatusExitComplete;
    $[103] = stagePortalTarget;
    $[104] = statusPhase;
    $[105] = t47;
  } else {
    t47 = $[105];
  }
  let t48;
  if ($[106] !== activePresetIndex || $[107] !== controlsPortalTarget || $[108] !== controlsStyle || $[109] !== dragGuideTone || $[110] !== frames || $[111] !== handlePresetInteractionStart || $[112] !== hasFinePointer || $[113] !== isDragGuideVisible || $[114] !== isInteractive || $[115] !== isPresetRotating || $[116] !== presetIndexes || $[117] !== rotateToPreset || $[118] !== shouldRevealPresets || $[119] !== useCustomDragCursor) {
    t48 = controlsPortalTarget ? createPortal(<Container className="pointer-events-none absolute inset-x-0 -top-px bottom-0 z-30 flex flex-col items-center bg-(--inline-threesixty-controls-background) lg:bg-transparent" style={controlsStyle} {...createDataAttributes({
      test: SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyControls
    })}>{hasFinePointer !== undefined && !useCustomDragCursor ? <SpartanProductExteriorThreesixtyDragGuide isVisible={isDragGuideVisible} tone={dragGuideTone} /> : null}<div className="xs:py-6 flex h-full w-full items-start justify-center py-4 md:py-8 lg:py-10" {...createDataAttributes({
        test: SPARTAN_PRODUCT_EXTERIOR_TRACKING.threesixtyPresetsSlot,
        "preset-animation": shouldRevealPresets ? "reveal" : "held"
      })}><AnimatePresence>{shouldRevealPresets ? <SpartanProductExteriorThreesixtyPresetThumbnails key="preset-thumbnails" frames={frames} indexes={presetIndexes} activeIndex={activePresetIndex} disabled={!isInteractive || isPresetRotating} onInteractionStart={handlePresetInteractionStart} onPointerEnter={() => setIsPointerInside(false)} onSelect={rotateToPreset} /> : null}</AnimatePresence></div></Container>, controlsPortalTarget) : null;
    $[106] = activePresetIndex;
    $[107] = controlsPortalTarget;
    $[108] = controlsStyle;
    $[109] = dragGuideTone;
    $[110] = frames;
    $[111] = handlePresetInteractionStart;
    $[112] = hasFinePointer;
    $[113] = isDragGuideVisible;
    $[114] = isInteractive;
    $[115] = isPresetRotating;
    $[116] = presetIndexes;
    $[117] = rotateToPreset;
    $[118] = shouldRevealPresets;
    $[119] = useCustomDragCursor;
    $[120] = t48;
  } else {
    t48 = $[120];
  }
  let t49;
  if ($[121] !== t42 || $[122] !== t43 || $[123] !== t44 || $[124] !== t45 || $[125] !== t46 || $[126] !== t47 || $[127] !== t48) {
    t49 = <div ref={mediaElementRef} className="relative flex size-full items-center" style={t42} aria-busy={t43}>{t44}{t45}{t46}{t47}{t48}</div>;
    $[121] = t42;
    $[122] = t43;
    $[123] = t44;
    $[124] = t45;
    $[125] = t46;
    $[126] = t47;
    $[127] = t48;
    $[128] = t49;
  } else {
    t49 = $[128];
  }
  return t49;
};